<script setup lang="ts">
import {defineAsyncComponent, onMounted, onUnmounted, ref} from 'vue'
import {MapsData, MarkerData} from '@/interfaces/MapsViewInterface.ts'
import mapMarkerImgUrl from '../assets/MapMarker.png'
import {getTranslation, setShop, loadGoogleMaps} from '@/ts/utilities.ts'


const DealerCard = defineAsyncComponent(() => import('@/components/DealerCard.vue'))

interface Props {
	mapsData: MapsData
}

declare global {
	interface Window {
		initMap: () => void
	}
}

declare let Cookiebot: any

const props = defineProps<Props>()

const mapLoaded = ref(false)

const mapsData: MapsData = {
	apiKey: props.mapsData.apiKey,
	endpoint: '/mobler-api/v2/shops/getshops',
}

const mapContainer = ref(null)
let map = null
let dealers: MarkerData[] = ref([])
let infoWindows: object[] = ref([])
// Guards against initMap() running more than once, in case loadMap() is
// triggered repeatedly (e.g. CookiebotOnAccept firing multiple times).
let mapInitStarted = false

const viewShopText = getTranslation('FindShopCTA')
const openingHourstext = getTranslation('FindShopOpeningHoursHeader')

const handleBodyClick = (event: Event) => {
	if (event.target && (event.target as Element).matches('.js-shop-link')) {
		event.preventDefault();
		const target = event.target as HTMLElement
		const marker: MarkerData = dealers.value.find(d => d.MenuText === target.dataset.dealer);
		const listElm = document.querySelector(`[data-dealer-card="${target.dataset.dealer}"]`);
		if(listElm !== null){
			const button = listElm.querySelector('button[type="button"]');
			if(button !== null){
				(button as HTMLElement).click();
			} else {
				setShop(marker);
			}
		} else {
			setShop(marker);
		}
	}
}

const initMap = async () => {
	if (mapInitStarted) return
	mapInitStarted = true

	// Defensive patch for a Google Maps internal tile-recycling bug
	// (https://issuetracker.google.com/issues/35821442).
	// During zoom animations Maps calls removeChild on a node that has already
	// been re-parented by its own tile recycler, producing an uncaught
	// NotFoundError. The patch silently ignores that specific case while
	// leaving all normal removeChild calls unaffected.
	if (!(Node.prototype as any).__removeChildPatched) {
		const _removeChild = Node.prototype.removeChild
		Node.prototype.removeChild = function <T extends Node>(child: T): T {
			if (child.parentNode !== this) return child
			return _removeChild.call(this, child) as T
		}
		;(Node.prototype as any).__removeChildPatched = true
	}

	// Load both libraries in parallel so they are both in the bootstrap
	// loader's library Set before the Maps script URL is built. Loading them
	// sequentially means 'marker' arrives after the URL is already fixed,
	// causing a second async parse cycle whose module initialisation races
	// the tile animation loop during zoom-in.
	const [{ Map, InfoWindow }, { AdvancedMarkerElement }] = await Promise.all([
		google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>,
		google.maps.importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
	])

	const markersData: MarkerData[] = dealers.value
	let zoom = 15
	if (markersData.length > 3) {
		zoom = 7
	}
	const center: google.maps.LatLngLiteral = {lat: 55.977675, lng: 10.933507}

	map = new Map(mapContainer.value, {
		center,
		zoom,
		disableDefaultUI: true,
		mapId: 'FindShop',
		// 'greedy' makes sure the map always handles pinch/drag gestures itself
		// instead of sometimes deferring to the page. Without this, pinch-zoom
		// on mobile can get "stuck" between the browser and the map, causing
		// markers to drift out of position and the map to stop responding to
		// further pinch gestures.
		gestureHandling: 'greedy'
	})

	markersData.forEach((dealer: MarkerData) => {

		let contentString = `
				<div class="flex flex-col gap-y-4 p-2 min-w-60 text-sm">
					<div class="p-4 absolute -right-6 -top-6 bg-white rounded-full shadow cursor-pointer w-12 h-12 flex justify-center items-center">
						<i class="fal fa-times text-black text-lg"></i>
					</div>
					<address class="not-italic">
						<p class="text-lg">${dealer.ShopName}</p>
						${ dealer.Address }<br>
						${ dealer.PostalAndCity }<br>
						${ dealer.Phone }<br>
						<a href="mailto:${dealer.Email}" class="text-black underline">
							${ dealer.Email }
						</a>
					</address>

					${dealer.ShopInformationList?.length > 0 ? `
						<div>
							<strong>${openingHourstext}</strong>
							${dealer.ShopInformationList.map(row => `
								 <div class="flex gap-x-2">
									${row.columnOne ? `<div class="${row.columnTwo ? 'basis-1/2' : ''}">${row.columnOne}</div>` : ''}
									${row.columnTwo ? `<div>${row.columnTwo}</div>` : ''}
								</div>
							`).join('')}
						</div>
					` : '' }

					${dealer.ShopInformationText ? `
						<div v-html="${dealer?.ShopInformationText}"></div>
					` : ''}

					<div class="flex">
						<a href="${dealer.Link}" class="cursor-pointer rounded-full hover:no-underline focus:no-underline rounded-full bg-primary text-white hover:bg-primary-dark active:border-primary-darker px-6 py-2 flex items-center justify-center gap-2 font-bold disabled:opacity-25 js-shop-link" data-dealer="${dealer.MenuText}">
							${viewShopText}
						</a>
					</div>
				</div>
				`

		let infowindow = new InfoWindow({
			content: contentString
		})

		infoWindows.value.push(infowindow)

		let latLng = {lat: dealer.Lat, lng: dealer.Lng}
		const image = document.createElement('img')
		// Explicit size prevents a layout shift once the image has loaded,
		// which otherwise moves the marker's anchor point (visible as markers
		// "jumping" when zooming, most noticeable on iOS Safari).
		image.width = 34
		image.height = 52
		image.decoding = 'sync'
		image.alt = dealer.ShopName ?? ''
		image.src = mapMarkerImgUrl


		let marker = new AdvancedMarkerElement({
			map,
			position: latLng,
			content: image
		})
		marker.addListener('click', function () {
			for (let i = 0; i < infoWindows.value.length; i++) {
				infoWindows.value[i].close()
			}
			infowindow.open(map, marker)
		})

	})

	document.body.addEventListener('click', handleBodyClick)

	let bounds = new google.maps.LatLngBounds()
	if (dealers.value.length > 1) {
		dealers.value.forEach((dealer: MarkerData) => {
			bounds.extend({lat: dealer.Lat, lng: dealer.Lng})
		})
		map.fitBounds(bounds)
	} else if (dealers.value.length === 1) {
		map.setCenter({lat: dealers.value[0].Lat, lng: dealers.value[0].Lng})
	}
}

const getDealers = async () => {
	return fetch(mapsData.endpoint).then(resp => resp.json())
}

const loadMap = () => {
	loadGoogleMaps(mapsData.apiKey).then(() => {
		initMap()
	})
	mapLoaded.value = true
}

onMounted(() => {
	getDealers().then((markersData: MarkerData[]) => {
		markersData?.shops?.forEach((markerData: MarkerData) => {
			dealers?.value.push(markerData)
		})
		if (Cookiebot.consent.marketing && Cookiebot.consent.statistics) {
			loadMap()
		} else {
			window.addEventListener('CookiebotOnAccept', function () {
				if (Cookiebot.consent.marketing && Cookiebot.consent.statistics) {
					loadMap()
				}
			}, false)
		}
	})
})

onUnmounted(() => {
	document.body.removeEventListener('click', handleBodyClick)
})

const RenderAction = defineAsyncComponent(() => import('@/components/helpers/RenderAction.vue'))
const renewCookies = () => {
	Cookiebot.renew()
}


</script>

<template>
	<div id="maps">
		<div class="container mb-8">
			<div
				ref="mapContainer"
				class="block min-h-[40rem] all-shops cookieconsent-optin-statistics cookieconsent-optin-marketing touch-none">
			</div>
			<div
				v-if="!mapLoaded"
				class="flex flex-col mt-3 mb-5 p-5 bg-warning rounded cookieconsent-optout-statistics cookieconsent-optout-marketing">
				<div class="p-2 text-center">{{ getTranslation('BlockedContentAllowCookiesText') }}</div>
				<div class="p-2 text-center">
					<a class="md:ml-auto px-6 py-2 rounded-full bg-black text-white hover:bg-black/90" href="javascript:Cookiebot.renew()">{{ getTranslation('ChangeCookieSettingsButtonText') }}</a>
				</div>
				<RenderAction
					action="button"
					theme="dark"
					size="sm"
					@click="renewCookies">
					{{ getTranslation('ChangeCookieSettingsButtonText') }}
				</RenderAction>
			</div>
		</div>

		<div class="bg-grey-light py-8">
			<div class="container">
				<div class="grid sm:grid-cols-1 md:grid-cols-4 gap-4">
					<DealerCard
						v-for="dealer in dealers"
						:key="dealer.ShopName"
						:dealer="dealer"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
